import { PDFDocument, PDFFont, PDFPage, RGB } from "pdf-lib";

/**
 * Types pour les formats de papier supportés
 */
export type PaperFormat = "A4" | "A3" | "US-Letter" | "US-Legal";

/**
 * Types pour l'orientation du papier
 */
export type Orientation = "portrait" | "landscape";

/**
 * Types pour les langues supportées
 */
export type Language = "fr" | "en" | "es" | "de" | "it" | "pt" | "";

/**
 * Types pour les versions TDA
 */
export type TdaVersion = "2024" | "2022" | "2019";

/**
 * Types pour les presets de configuration
 */
export type ConfigPreset = "basic" | "alinekarl" | "short";

/**
 * Types pour l'alignement du texte
 */
export type TextAlign = "left" | "center" | "right" | "justify";

/**
 * Types pour les familles de polices supportées (3 familles principales)
 */
export type FontFamily = "TimesRoman" | "Helvetica" | "Courier";

/**
 * Types pour les constantes de type de règle
 */
export const enum RuleType {
  CHAPTER = 1,
  HEADING = 2,
  RULE = 3,
  RP = 4,
  IA = 5,
  INFO = 6,
}

/**
 * Interface pour les données d'une règle
 */
export interface RuleData {
  id: string;
  title: string;
  text: string;
  type: RuleType;
}

/**
 * Interface pour les métadonnées des règles TDA
 */
export interface TdaRulesMeta {
  version: string;
  pdf_title: string;
  rule_title: string;
  annexe?: string;
  author?: string;
  footer?: string[];
  rulesToTrace?: string[];
  // logo
  logo_path?: string;
  logo_ratio?: number;
}

/**
 * Interface pour les données complètes des règles TDA
 */
export interface TdaRulesData {
  meta: TdaRulesMeta;
  rules: RuleData[];
}

/**
 * interface familly for font
 */
export interface FontVariants {
  regular: PDFFont;
  bold: PDFFont;
  italic: PDFFont;
  boldItalic: PDFFont;
  graphic: PDFFont;
}

/**
 * page size
 */
export interface PageSize {
  width: number;
  height: number;
  marginTop: number;
  marginBottom: number;
  marginLeft: number;
  marginRight: number;
}

/**
 * Interface pour les options du générateur PDF
 */
export interface PdfGeneratorOptions {
  // Format et orientation
  paperFormat: PaperFormat;
  orientation: Orientation;

  // Langue et version
  lang: Language;
  version?: TdaVersion;

  // Dimensions calculées
  pageSize: PageSize;

  // Marges
  textOffset: number;
  // Marg pour la config uniquement
  topMargin: number;
  bottomMargin: number;
  leftMargin: number;
  rightMargin: number;

  // with border
  showBorders?: boolean;
  borderWidth: number;
  borderOpacity: number;
  borderInterval: number;

  // Polices (4 variantes par famille)
  fonts: FontVariants;
  fontFamily: FontFamily;

  // Logo
  logoPath?: string;
  logoWidth: number;
  logoRatio: number;

  // Tailles de police
  mainTitleFontSize: number;
  ruleFontSize: number;
  ruleTitleFontSize: number;
  chapterFontSize: number;

  // Titre principal
  mainTitle: string;

  // Mise en page
  textAlign: TextAlign;
  lineSpacing: number;
  shiftSize: number;
  columnCount: number;

  // Options de contenu
  includeProcedures: boolean;
  includeIndex: boolean;
  includeGlossary: boolean;
  indexAt: "start" | "end";
  indexOptions?: {
    lineSpacing: number;
    titleRuleSize: number;
    sectionRuleSize: number;
    chapterRuleSize: number;
    columnCount: number;
    fontFamily: FontFamily;
    fonts?: FontVariants;
  };
  includeExamples: boolean;
  examples?: {
    inline: boolean;
    fontSize: number;
    lineSpacing: number;
    marginX: number;
    marginY: number;
    bgColor: string;
    borderColor: string;
    radius: number;
    opacity: number;
  };

  // Footer
  marginBottomFontSize: number;
  marginBottomColor: any; // Changed from RGB to any
  // Header
  marginTopFontSize: number;
  marginTopColor: any; // Changed from RGB to any

  // Preset de configuration
  configPreset?: ConfigPreset;
}

/**
 * Interface pour les options partielles (utilisateur)
 */
export interface UserPdfOptions extends Partial<PdfGeneratorOptions> {
  // Propriétés obligatoires pour l'utilisateur
  paperFormat?: PaperFormat;
  orientation?: Orientation;
  lang?: Language;
}

/**
 * Interface pour les options de formatage de texte
 */
export interface TextFormattingOptions {
  fontSize: number;
  fonts: FontVariants;
  maxWidth: number;
  lineSpacing: number;
  textAlign: TextAlign;
  shiftSize: number;
}

/**
 * Interface pour un segment de texte formaté
 */
export interface TextSegment {
  text: string;
  bold: boolean;
  italic: boolean;
  graphic: boolean;
  underline: boolean;
  nonSeparable: boolean;
  x?: number;
  y?: number;
  width?: number;
  font?: PDFFont;
  size?: number;
  link: boolean;
  linkId?: string | null;
}

/**
 * Interface pour une ligne formatée
 */
export interface FormattedLine {
  segments: TextSegment[];
  height: number;
  paddingTop: number;
}

/**
 * Interface pour un groupe de segments
 */
export interface SegmentsGroup {
  segments: FormattedLine[];
  height: number;
}

/**
 * Interface pour les options de bloc de texte
 */
export interface TextBlockOptions {
  currentPage: PDFPage;
  pdfDoc: PDFDocument;
  options: PdfGeneratorOptions;
  rule: RuleData;
  yPosition: number;
  marginX?: number;
  paddingX?: number;
  paddingY?: number;
  withTitle?: boolean;
  withBox?: boolean;
  bgColor?: RGB;
  borderColor?: RGB;
  radius?: number;
}

/**
 * Interface pour le retour de bloc de texte
 */
export interface TextBlockResult {
  positionX: number;
  positionY: number;
  currentPage: PDFPage;
}

/**
 * Interface pour les options d'en-tête
 */
export interface HeaderOptions {
  page?: PDFPage | null;
  topPosition: number;
  currentChapter?: RuleData;
  radius?: number;
  small?: boolean;
}

/**
 * Interface pour le retour d'en-tête
 */
export interface HeaderResult {
  yPosition: number;
  currentPage: PDFPage;
}

/**
 * Interface pour les options de titre
 */
export interface TitleOptions {
  page: PDFPage;
  x: number;
  y: number;
  font: PDFFont;
  size: number;
  maxWidth: number;
  lineSpacing?: number;
  align?: TextAlign;
  color?: RGB;
  opacity?: number;
}

/**
 * Interface pour les options de segments formatés
 */
export interface FormattedSegmentsOptions {
  x: number;
  y: number;
  startY: number;
  fontSize: number;
  canAddPage?: boolean;
  bottomOffset?: number;
}

/**
 * Interface pour le retour de segments formatés
 */
export interface FormattedSegmentsResult {
  currentY: number;
  currentX: number;
  currentPage: PDFPage;
}

/**
 * Interface pour une ligne wrappée
 */
export interface WrappedLine {
  content: string;
  indent: number;
  firstIndent: number;
  paddingTop: number;
  paddingBottom: number;
}

/**
 * Interface pour les résultats de calcul de texte formaté
 */
export interface FormattedTextResult {
  listSegments: FormattedLine[];
  totalHeight: number;
  firstSegmentsHeight: number;
}

/**
 * Default configuration
 */
export const defaultConfig = {
  version: "2024",
  lang: "",
  configPreset: "",
  paperFormat: "A4",
  orientation: "portrait",
  columnCount: 1,

  // Marges et mise en page
  topMargin: 50,
  bottomMargin: 50,
  leftMargin: 40,
  rightMargin: 40,
  textOffset: 10,

  // Border options
  showBorders: false,
  borderWidth: 10,
  borderOpacity: 0.4,
  borderInterval: 6,

  // Typographie
  mainTitle: "POKER TOURNAMENT DIRECTORS ASSN.",
  mainTitleFontSize: 18,
  ruleTitleFontSize: 13,
  chapterFontSize: 15,
  ruleFontSize: 11,
  textAlign: "left",
  lineSpacing: 1.2,
  logoWidth: 120,
  logoRatio: 0.5,

  // Contenu
  includeProcedures: true,
  includeExamples: true,
  includeGlossary: false,
  examples: {
    inline: false,
    fontSize: 10,
    lineSpacing: 1.1,
    marginX: 4,
    bgColor: "#f9f9f2",
    borderColor: "#e6e680",
    radius: 12,
    opacity: 0.8,
  },
  includeIndex: false,
  indexAt: "end",
  indexOptions: {
    lineSpacing: 1.1,
    titleRuleSize: 12,
    sectionRuleSize: 13,
    chapterRuleSize: 14,
    columnCount: 2,
    fontFamily: "TimesRoman",
  },
};
